import {
  sanatizeNavigationUrl,
  useCompareUrlWithProjectId,
  useNavigateWithProjectId,
} from 'common/hooks/route';
import {
  ANALYZE_ROUTES,
  CAPTURE_ROUTES,
  DEVELOPMENT_ROUTES,
  PROFILE_CAPTURE_ROUTES,
  REPORTS_ROUTES,
} from './routes.const';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { useResize } from 'common/hooks/resize';
import { useSelector } from 'react-redux';
import { useState } from 'react';
import { selectModules } from 'common/store/slices/page-config.slice';
import { PROJECT_ID_PLACEHOLDER, PROJECT_ID_PREFIX } from 'common/constants';
import useNavigationMenus from 'common/hooks/useMenus';
import { useModuleProjectContext } from 'common/contexts/useModuleProjectContext';
import { PLANNING_ROUTES } from './routes.planning.const';
import useDevMode from 'common/hooks/useDevMode';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const devMode = useDevMode();
  const [showDesktopOnlyScreen, setShowDesktopOnlyScreen] = useState<boolean>(false);
  const { search } = useLocation();
  const { module, projectId, page } = useParams();
  const { compareUrl } = useCompareUrlWithProjectId();
  const navigate = useNavigateWithProjectId();
  const { projectPages } = useNavigationMenus();
  const { isMobile } = useResize();
  const { activeModule, activeProject, captureProjects } = useModuleProjectContext();
  const modules = useSelector(selectModules);

  const buildUrl = (url: string = '') => {
    return sanatizeNavigationUrl({
      url: url,
      urlProjectId: projectId ?? '',
      activeProjectId: activeProject?.moduleProjectID ?? '',
    });
  };

  // Global redirects (keeping as switch statement for future use)
  switch (true) {
    case compareUrl(ANALYZE_ROUTES.responseDetails):
      navigate(ANALYZE_ROUTES.themeAnalytics, { replace: true });
      return <></>;
    default:
      break;
  }

  // If the user is on a development route, skip the route protection
  if (devMode && compareUrl([...Object.values(DEVELOPMENT_ROUTES)])) {
    return <>{children}</>;
  }

  // Profile Capture owns its no-project permission screen. This is the one
  // profile-capture case that must pass the normal page authorization check:
  // there is intentionally no active project or configured project page.
  if (!captureProjects.length && compareUrl(PROFILE_CAPTURE_ROUTES.root)) {
    return <>{children}</>;
  }

  // If a user is on a mobile device, they always get re-directed to the capture page
  if (isMobile && !compareUrl([DEVELOPMENT_ROUTES.root, CAPTURE_ROUTES.root], { includes: true })) {
    const firstCaptureModule = modules.find((module) =>
      module.projects.find((project) =>
        project.pages.find((page) => page.Route?.includes(CAPTURE_ROUTES.root))
      )
    );

    const captureProject = firstCaptureModule?.projects.find((project) =>
      project.pages.find((page) => page.Route?.includes(CAPTURE_ROUTES.root))
    );

    if (!captureProject && !showDesktopOnlyScreen) {
      setShowDesktopOnlyScreen(true);
    }

    if (!showDesktopOnlyScreen && captureProject) {
      const sanitizedUrl = CAPTURE_ROUTES.root.replace(
        PROJECT_ID_PLACEHOLDER,
        `${PROJECT_ID_PREFIX}${captureProject.moduleProjectID}`
      );
      return <Navigate to={`${sanitizedUrl}${search}`} replace={true} />;
    }
  }

  // These pages don't have pages configured in DB, so allow all child pages, filtered to allowed pages
  const authorizeChildPages = [
    REPORTS_ROUTES.root,
    CAPTURE_ROUTES.root,
    PROFILE_CAPTURE_ROUTES.root,
    PLANNING_ROUTES.root,
    ANALYZE_ROUTES.elsieai,
  ].filter((page) => projectPages.find((projectPage) => projectPage.Route?.startsWith(page)));

  const pageAuthorized: boolean =
    !!projectPages.find((page) => compareUrl(page.Route)) ||
    !!authorizeChildPages.find((page) => compareUrl(page, { startsWith: true })) ||
    (compareUrl(PROFILE_CAPTURE_ROUTES.root, { includes: true }) &&
      !!projectPages.find((page) => page.Route === CAPTURE_ROUTES.root));

  if (!pageAuthorized) {
    // If the user hasn't set a project ID, find first project and page, and redirect to it
    if (module && !projectId) {
      const firstProject = activeModule?.projects[0];
      const firstRoute = firstProject?.pages[0].Route;

      return <Navigate to={`${buildUrl(firstRoute)}`} replace={true} />;
    }

    // If the user has a module & project ID but no page, find the first page and redirect to it
    if (module && projectId && !page) {
      const firstRoute = activeProject?.pages[0].Route;

      return <Navigate to={`${buildUrl(firstRoute)}`} replace={true} />;
    }

    return (
      <Navigate to={`${activeModule?.route}/${activeProject?.moduleProjectID}`} replace={true} />
    );
  }

  return <>{children}</>;
}

export default ProtectedRoute;
